import { ChevronRight } from 'lucide-react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { PortalWorkspaceDetail, PortalWorkspaceMember } from '../types/users.types';
import {
  formatStatusLabel,
  formatSubscriptionLabel,
  getSubscriptionBadgeVariant,
  resolvePortalUserUsage,
} from '../utils/portal-users.mappers';
import { UsageBreakdownCell } from './usage-breakdown-cell';

const STATUS_VARIANTS: Record<PortalWorkspaceMember['status'], PortalDsBadgeVariant> = {
  active: 'green',
  blocked: 'red',
};

interface TeamMemberListProps {
  workspace: PortalWorkspaceDetail;
  onMemberClick: (member: PortalWorkspaceMember) => void;
}

export function TeamMemberList({ workspace, onMemberClick }: TeamMemberListProps) {
  return (
    <ul className="space-y-2">
      {workspace.members.map((member) => (
        <li key={member.id}>
          <button
            type="button"
            className={cn(
              'flex w-full items-start justify-between gap-4 rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3 text-left transition-colors',
              'hover:border-[#D9DBE3] hover:bg-white',
            )}
            onClick={() => onMemberClick(member)}
          >
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[14px] font-semibold text-[#0D0F1A]">
                  {member.name}
                </span>
                {member.is_guest ? (
                  <PortalDsBadge variant="amber" pill>
                    Guest
                  </PortalDsBadge>
                ) : null}
                <PortalDsBadge
                  variant={STATUS_VARIANTS[member.status]}
                  pill
                >
                  {formatStatusLabel(member.status)}
                </PortalDsBadge>
                <PortalDsBadge
                  variant={getSubscriptionBadgeVariant(member.subscription)}
                  pill
                >
                  {formatSubscriptionLabel(member.subscription)}
                </PortalDsBadge>
              </div>
              <p className="text-[13px] text-[#5E6278]">
                {member.email}
              </p>
              <UsageBreakdownCell usage={resolvePortalUserUsage(member)} />
            </div>
            <ChevronRight className="mt-1 size-4 shrink-0 text-[#9CA3AF]" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}
